import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  BeakerIcon,
  CloudArrowUpIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { MainLayout } from '../layouts';
import { useFeatureFlags, type FeatureFlag } from '../contexts/FeatureFlagContext';
import { logger } from '../lib/logging';
import { useAuth } from '../contexts/AuthContext';

interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface FlagInfo {
  key: FeatureFlag;
  label: string;
  description: string;
  value: boolean;
  scope: 'env' | 'user';
}

interface AdminFeatureFlagsProps {
  embedded?: boolean;
  correlationId?: string;
}

// Feature flag to page mappings
const FLAG_PAGES: Record<string, Array<{path: string, label: string}>> = {
  PAYWALL: [
    { path: '/compare', label: 'Comparison Setup' }
  ],
  TOKENUSAGE: [
    { path: '/results', label: 'Results Pages (Token Tracking)' },
    { path: '/compare', label: 'Comparison Setup (Consensus)' }
  ],
  PM: [
    { path: '/admin', label: 'Admin Panel (Payment Settings)' }
  ]
};

// Flag metadata
const FLAG_METADATA: Record<FeatureFlag, { label: string; description: string }> = {
  ANONYMOUS: {
    label: 'Anonymous Users',
    description: 'Enable anonymous user comparison creation without login (disabled = auth required)'
  },
  PAYWALL: {
    label: 'Paywall',
    description: 'Paywall experience gating free-tier messaging on comparison setup'
  },
  TOKENUSAGE: {
    label: 'Token Usage Tracking',
    description: 'Enable token tracking for consensus analysis and explanation generation'
  },
  PM: {
    label: 'Payment Management',
    description: 'Enable Stripe integration and payment processing capabilities'
  },
  BETA: {
    label: 'Beta Access',
    description: 'Controls beta user access and restriction flows'
  }
};

export const AdminFeatureFlags: React.FC<AdminFeatureFlagsProps> = ({ embedded = false, correlationId }) => {
  const { isEnabled, refresh, loading: contextLoading, error: contextError, lastFetch, clearLocalOverride, clearAllLocalOverrides } = useFeatureFlags();
  const { user } = useAuth();

  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [mode, setMode] = useState<'remote' | 'local'>('remote');
  const [testingAPI, setTestingAPI] = useState(false);
  const [updatingFlag, setUpdatingFlag] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const getAuthToken = async (): Promise<string> => {
    if (!user) {
      throw new Error('Authentication required');
    }
    return user.getIdToken();
  };

  const correlationIdValue = useMemo(() => correlationId ?? crypto.randomUUID(), [correlationId]);
  const ctx = useMemo(() => logger.withCorrelation(correlationIdValue), [correlationIdValue]);

  // Track when we've loaded at least once to prevent infinite loading
  useEffect(() => {
    if (!contextLoading && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [contextLoading, hasLoadedOnce]);

  // Build flags array from context
  const flags: FlagInfo[] = Object.entries(FLAG_METADATA).map(([key, metadata]) => ({
    key: key as FeatureFlag,
    label: metadata.label,
    description: metadata.description,
    value: isEnabled(key as FeatureFlag),
    scope: mode === 'remote' ? 'env' : 'user'
  }));

  const addToast = (type: 'success' | 'error' | 'info', message: string) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };

  // Test backend API connection
  const testRemoteAPI = async (): Promise<{success: boolean; message: string}> => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || '';
      const endpoint = `${apiBaseUrl}/api/admin/flags`;

      logger.info('admin.test_api_start', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        endpoint
      });

      const authToken = await getAuthToken();

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }
      });

      if (!response.ok) {
        return {
          success: false,
          message: `API returned ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();

      if (!data.flags || !Array.isArray(data.flags)) {
        return {
          success: false,
          message: 'API returned invalid data format'
        };
      }

      logger.info('admin.test_api_success', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        flagCount: data.flags.length
      });

      return {
        success: true,
        message: `✅ Backend API working! Found ${data.flags.length} flags. Last fetch: ${lastFetch?.toLocaleTimeString() || 'N/A'}`
      };
    } catch (error) {
      logger.error('admin.test_api_error', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        err_message: error instanceof Error ? error.message : 'Unknown'
      });

      return {
        success: false,
        message: `Network error: ${error instanceof Error ? error.message : 'Unknown'}`
      };
    }
  };

  const handleTestAPI = async () => {
    setTestingAPI(true);
    const result = await testRemoteAPI();
    addToast(result.success ? 'success' : 'error', result.message);
    setTestingAPI(false);
  };

  const handleRefresh = async () => {
    logger.info('admin.manual_refresh', undefined, {
      ...ctx,
      component: 'AdminFeatureFlags'
    });

    await refresh();
    addToast('success', 'Flags refreshed from backend API');
  };

  const handleDiagnostic = () => {
    console.group('🔍 Feature Flag System Diagnostic');
    console.log('Mode:', mode);
    console.log('Context State:', {
      loading: contextLoading,
      error: contextError,
      lastFetch: lastFetch?.toISOString()
    });
    console.log('localStorage Keys:', Object.keys(localStorage).filter(k => k.startsWith('feature_flag_')));
    console.log('Current Flag Values:', {
      ANONYMOUS: isEnabled('ANONYMOUS'),
      PAYWALL: isEnabled('PAYWALL'),
      TOKENUSAGE: isEnabled('TOKENUSAGE'),
      PM: isEnabled('PM'),
      BETA: isEnabled('BETA')
    });
    console.log('API Endpoint:', import.meta.env.VITE_API_URL || '(relative)');
    console.groupEnd();

    addToast('info', 'Diagnostic info logged to console (press F12)');
  };

  const handleToggleLocal = (flagKey: string, newValue: boolean) => {
    if (mode === 'remote') {
      addToast('error', '❌ Cannot toggle in Remote Mode. Use Vercel Dashboard or switch to Local Mode.');
      return;
    }

    // Local mode: set localStorage override
    localStorage.setItem(`feature_flag_${flagKey}`, newValue.toString());

    // Force re-render by toggling mode state
    setMode('local');

    addToast('success', `${flagKey} ${newValue ? 'enabled' : 'disabled'} (LOCAL BROWSER ONLY)`);

    logger.info('admin.toggle_local', undefined, {
      ...ctx,
      component: 'AdminFeatureFlags',
      flagKey,
      newValue
    });
  };

  const handleToggleRemote = async (flagKey: string, newValue: boolean) => {
    setUpdatingFlag(flagKey);

    try {
      logger.info('admin.toggle_remote_start', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        flagKey,
        newValue
      });

      const apiBaseUrl = import.meta.env.VITE_API_URL || '';
      const authToken = await getAuthToken();

      const response = await fetch(`${apiBaseUrl}/api/admin/flags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ key: flagKey, value: newValue })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Clear localStorage override for this flag to ensure database value takes precedence
      clearLocalOverride(flagKey as FeatureFlag);

      await refresh(); // Refresh flags from backend
      addToast('success', `✅ ${flagKey} ${newValue ? 'enabled' : 'disabled'} for ALL users`);

      logger.info('admin.toggle_remote_success', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        flagKey,
        newValue,
        action: 'cleared_localStorage_override'
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addToast('error', `❌ Failed to toggle ${flagKey}: ${errorMessage}`);

      logger.error('admin.toggle_remote_error', undefined, {
        ...ctx,
        component: 'AdminFeatureFlags',
        flagKey,
        newValue,
        err_message: errorMessage
      });
    } finally {
      setUpdatingFlag(null);
    }
  };

  // Show loading only on first load, not on subsequent refreshes
  if (contextLoading && !hasLoadedOnce) {
    const loadingContent = (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-slate-700/50 rounded w-1/3 mb-4"></div>
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-slate-700/50 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return embedded ? loadingContent : (
      <MainLayout showBreadcrumb={true} breadcrumbText="Admin / Feature Flags">
        {loadingContent}
      </MainLayout>
    );
  }

  const pageContent = (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <CogIcon className="w-8 h-8 text-cyan-400" />
                <h1 className="text-3xl font-bold text-white">Feature Flags</h1>
                <span className="px-3 py-1 text-sm bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 rounded-full">
                  {mode === 'remote' ? '🌐 Remote Control' : '💻 Local Mode'}
                </span>
              </div>

              {/* Mode Switcher */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-slate-400">Mode:</span>
                <button
                  onClick={() => {
                    // When switching to Remote mode, clear all localStorage overrides
                    if (mode !== 'remote') {
                      clearAllLocalOverrides();
                      logger.info('admin.switch_to_remote', undefined, {
                        ...ctx,
                        component: 'AdminFeatureFlags',
                        action: 'cleared_localStorage'
                      });
                    }
                    setMode('remote');
                  }}
                  className={`px-3 py-1 text-sm rounded-l-lg border transition-colors ${
                    mode === 'remote'
                      ? 'bg-cyan-600 text-white border-cyan-500'
                      : 'bg-slate-700/50 text-slate-300 border-slate-600 hover:bg-slate-600'
                  }`}
                >
                  Remote
                </button>
                <button
                  onClick={() => setMode('local')}
                  className={`px-3 py-1 text-sm rounded-r-lg border transition-colors ${
                    mode === 'local'
                      ? 'bg-purple-600 text-white border-purple-500'
                      : 'bg-slate-700/50 text-slate-300 border-slate-600 hover:bg-slate-600'
                  }`}
                >
                  Local
                </button>
              </div>
            </div>

            <p className="text-slate-400">
              {mode === 'remote' ? (
                <>
                  <strong>Remote Control Mode:</strong> Flags are fetched from backend API and controlled via database.
                  Changes affect <span className="text-cyan-400">ALL users globally</span>.
                </>
              ) : (
                <>
                  <strong>Local Mode:</strong> Test flags in <span className="text-purple-400">this browser only</span> using localStorage.
                  Does not affect other users.
                </>
              )}
            </p>
          </motion.div>

        {/* Feature Flags Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden mb-6"
        >
          <div className="p-6 border-b border-slate-700/50">
            <h2 className="text-xl font-semibold text-white mb-1">Available Flags</h2>
            <p className="text-sm text-slate-400">
              {flags.length} feature flags configured • Last fetch: {lastFetch?.toLocaleTimeString() || 'Never'}
            </p>
          </div>

          <div className="divide-y divide-slate-700/50">
            {flags.map((flag, index) => (
              <motion.div
                key={flag.key}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 hover:bg-slate-700/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-3 h-3 rounded-full ${
                        flag.value ? 'bg-green-400' : 'bg-gray-500'
                      }`} />
                      <h3 className="text-lg font-medium text-white">
                        {flag.label}
                      </h3>
                      <span className="px-2 py-1 text-xs bg-slate-700/50 text-slate-300 rounded">
                        {flag.scope === 'env' ? 'Remote' : 'Local'}
                      </span>
                    </div>
                    {flag.description && (
                      <p className="text-sm text-slate-400 ml-6 mb-3">
                        {flag.description}
                      </p>
                    )}

                    {/* Page Links */}
                    {FLAG_PAGES[flag.key] && (
                      <div className="ml-6 mt-2">
                        <span className="text-xs font-medium text-slate-500 block mb-1">Controlled Pages:</span>
                        <div className="flex flex-wrap gap-2">
                          {FLAG_PAGES[flag.key].map((page, pageIndex) => (
                            <Link
                              key={pageIndex}
                              to={page.path}
                              className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-slate-700/30 hover:bg-slate-600/40 text-slate-300 hover:text-white rounded border border-slate-600/50 hover:border-slate-500/50 transition-all"
                            >
                              <span>{page.label}</span>
                              <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <span className={`text-sm font-medium ${
                      flag.value ? 'text-green-400' : 'text-gray-400'
                    }`}>
                      {flag.value ? 'Enabled' : 'Disabled'}
                    </span>

                    {mode === 'remote' ? (
                      <button
                        onClick={() => handleToggleRemote(flag.key, !flag.value)}
                        disabled={updatingFlag === flag.key}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 ${
                          flag.value
                            ? 'bg-cyan-600'
                            : 'bg-gray-600'
                        }`}
                        role="switch"
                        aria-checked={flag.value}
                        title={`Toggle ${flag.key} for ALL users`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            flag.value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleLocal(flag.key, !flag.value)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-slate-800 ${
                          flag.value
                            ? 'bg-purple-600'
                            : 'bg-gray-600'
                        }`}
                        role="switch"
                        aria-checked={flag.value}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            flag.value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Remote Control Instructions */}
        {mode === 'remote' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-6"
          >
            <h3 className="text-lg font-semibold text-cyan-300 mb-4 flex items-center">
              <GlobeAltIcon className="w-6 h-6 mr-2" />
              Remote Control for All Users
            </h3>

            <div className="space-y-4 text-sm text-cyan-100/80">
              <p>
                <strong>Database-Backed Feature Flags:</strong> Toggle switches above control features for <strong>ALL users</strong> instantly.
                Changes are persisted to the database and apply within seconds without redeploying.
              </p>

              <div className="bg-slate-800/50 rounded p-4">
                <p className="font-semibold text-cyan-300 mb-2">How It Works</p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Toggle any switch above to enable/disable a feature</li>
                  <li>Change is saved to PostgreSQL database immediately</li>
                  <li>All users receive the change within 60 seconds (auto-refresh)</li>
                  <li>No Vercel redeployment needed</li>
                </ol>
              </div>

              <div className="flex items-start space-x-2 mt-4 p-3 bg-slate-800/50 rounded">
                <InformationCircleIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-cyan-300">Current System Status:</p>
                  <ul className="mt-2 space-y-1 text-xs">
                    <li>Mode: <span className="text-cyan-400 font-mono">Database-Backed Remote Control</span></li>
                    <li>Last API Fetch: <span className="text-cyan-400 font-mono">{lastFetch?.toLocaleTimeString() || 'Loading...'}</span></li>
                    <li>Auto-refresh: <span className="text-cyan-400 font-mono">Every 60 seconds</span></li>
                    <li>Backend API: <span className="text-cyan-400 font-mono">{import.meta.env.VITE_API_URL || '(relative)'}/api/admin/flags</span></li>
                    <li>Database: <span className="text-cyan-400 font-mono">Neon PostgreSQL</span></li>
                    {contextError && <li className="text-red-400">⚠️ Error: {contextError}</li>}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Validation & Testing Panel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-6 bg-purple-900/20 border border-purple-700/50 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-purple-300 mb-4 flex items-center">
            <BeakerIcon className="w-6 h-6 mr-2" />
            Validate & Test System
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleTestAPI}
              disabled={testingAPI}
              className="px-4 py-3 bg-purple-600/30 hover:bg-purple-600/40 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <CloudArrowUpIcon className="w-5 h-5" />
              <span>{testingAPI ? '⏳ Testing...' : '🧪 Test API'}</span>
            </button>

            <button
              onClick={handleRefresh}
              className="px-4 py-3 bg-blue-600/30 hover:bg-blue-600/40 rounded-lg text-white font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ArrowPathIcon className="w-5 h-5" />
              <span>🔄 Refresh Flags</span>
            </button>

            <button
              onClick={handleDiagnostic}
              className="px-4 py-3 bg-green-600/30 hover:bg-green-600/40 rounded-lg text-white font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <InformationCircleIcon className="w-5 h-5" />
              <span>🔍 Diagnostics</span>
            </button>
          </div>

          {/* Test Results */}
          <div className="mt-4 p-4 bg-slate-800/50 rounded font-mono text-xs">
            <p className="text-slate-400 mb-2">System Health Check:</p>
            <div className="space-y-1 text-slate-300">
              <div>{contextLoading ? '⏳' : '✅'} FeatureFlagProvider: {contextLoading ? 'Loading...' : 'Active'}</div>
              <div>{contextError ? '❌' : '✅'} API Fetch: {contextError ? `Error: ${contextError}` : 'Working'}</div>
              <div>✅ Auto-refresh: Every 60s</div>
              <div>✅ Last Update: {lastFetch?.toLocaleTimeString() || 'Never'}</div>
              <div>🌐 Mode: {mode === 'remote' ? 'Remote Control (Database)' : 'Local Testing (localStorage)'}</div>
            </div>
          </div>
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4"
        >
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-400">How It Works</h3>
              <p className="text-sm text-blue-200/80 mt-1">
                <strong>Remote Mode:</strong> Flags are controlled via database and apply to ALL users globally.
                Frontend fetches from backend API every 60 seconds.
                <br /><br />
                <strong>Local Mode:</strong> Flags are stored in your browser's localStorage for testing.
                Changes only affect this browser and do not impact other users.
              </p>
            </div>
          </div>
        </motion.div>
        </div>

        {/* Toast Messages */}
        <div className="fixed bottom-6 right-6 space-y-2 z-50">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className={`px-4 py-3 rounded-lg shadow-lg backdrop-blur-sm border ${
                toast.type === 'success'
                  ? 'bg-green-900/90 border-green-700/50 text-green-100'
                  : toast.type === 'error'
                  ? 'bg-red-900/90 border-red-700/50 text-red-100'
                  : 'bg-blue-900/90 border-blue-700/50 text-blue-100'
              }`}
            >
              <div className="flex items-center space-x-2">
                {toast.type === 'success' ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                ) : toast.type === 'error' ? (
                  <XCircleIcon className="w-5 h-5 text-red-400" />
                ) : (
                  <InformationCircleIcon className="w-5 h-5 text-blue-400" />
                )}
                <span className="text-sm font-medium">{toast.message}</span>
              </div>
            </motion.div>
          ))}
      </div>
    </div>
  );

  return embedded ? pageContent : (
    <MainLayout showBreadcrumb={true} breadcrumbText="Admin / Feature Flags">
      {pageContent}
    </MainLayout>
  );
};

export default AdminFeatureFlags;
